/**
 * Pass 3 · Public Demo Banner + global axios 403 interceptor.
 *
 * Renders a thin banner whenever the current session is a public-demo
 * one, and translates the canonical backend 403 into a friendly toast
 * so prospects never see a raw error stack.
 */
import { useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const DEMO_BLOCKED_MSG = 'This action is disabled in the public demo.';

export default function PublicDemoBanner() {
  const { publicDemoSession, logout } = useAuth();

  // Register the global axios interceptor once — even if the user
  // isn't a public-demo session, having this in place is harmless.
  useEffect(() => {
    // Expose axios on window for programmatic acceptance tests. Harmless
    // in prod; simply lets a Playwright script trigger the interceptor.
    if (typeof window !== 'undefined') window.__axios = axios;
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 403 && detail === DEMO_BLOCKED_MSG) {
          toast.info(DEMO_BLOCKED_MSG);
        } else if (err?.response?.status === 401 &&
                    (detail || '').startsWith('Your demo session has ended')) {
          toast.info('Your demo session has ended. Redirecting…');
          setTimeout(() => { window.location.href = '/demo'; }, 700);
        }
        return Promise.reject(err);
      });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const remainingText = useMemo(() => {
    const exp = publicDemoSession?.expires_at;
    if (!exp) return null;
    const diffMs = new Date(exp) - new Date();
    if (diffMs <= 0) return 'ending shortly';
    const mins = Math.max(1, Math.floor(diffMs / 60000));
    return `${mins} min left`;
  }, [publicDemoSession?.expires_at]);

  if (!publicDemoSession) return null;
  return (
    <div data-testid="public-demo-banner"
          className="sticky top-0 z-[60] flex items-center gap-3
                     bg-[#143E30] text-white px-4 py-2 text-sm
                     shadow-sm">
      <span className="inline-flex items-center bg-white text-[#143E30]
                          rounded-full px-2 py-0.5 text-[10px] font-bold
                          uppercase tracking-wider">
        Interactive Demo
      </span>
      <span className="text-white/95">
        You're exploring The Amber Kitchen Group. Changes are disabled in the public demo.
      </span>
      {remainingText && (
        <span className="ml-auto text-white/80 text-xs">{remainingText}</span>
      )}
      <button onClick={logout} data-testid="public-demo-exit"
               className="border border-white/60 hover:bg-white/10
                          rounded px-2.5 py-1 text-xs font-medium">
        Exit demo
      </button>
    </div>
  );
}
